/**
 * DraftingPanel — orthographic drawing views of the selected node.
 *
 * Renders FRONT / TOP / RIGHT / ISO projections as plain SVG, drawn from the
 * manifest's bounding boxes. No extra WebGL context, no geometry download: the
 * panel is cheap enough to sit beside an 878k-triangle scene on a Pi.
 *
 * HONEST LIMITATION, stated in the UI too: these are **bounding-box extents**,
 * not true silhouettes. Selecting an assembly draws one rectangle per child
 * part, which reads as a real packaging layout; selecting a single part draws
 * one rectangle. Curved geometry is shown as its extents, not its outline.
 *
 * Frame (from the manifest): +X forward, +Y left, +Z up, millimetres.
 *   FRONT — looking rearward along -X : horizontal Y, vertical Z
 *   TOP   — looking down      along -Z : horizontal X, vertical Y
 *   RIGHT — looking from the right     : horizontal X, vertical Z
 * All three share ONE scale, as a drafting sheet would, so the views are
 * directly comparable.
 */

import React, { useMemo, useRef } from 'react';
import './DraftingPanel.css';

const VIEW_W = 250;
const VIEW_H = 150;
const PAD = 26;          // room for dimension lines
const ISO_COS = Math.cos(Math.PI / 6);
const ISO_SIN = Math.sin(Math.PI / 6);

/** Preferred drawing scales, picked like a draughtsman would. */
const SCALES = [1, 2, 2.5, 5, 10, 20, 25, 50, 100, 200, 500, 1000];

/**
 * Cap on rectangles drawn per sheet. The compact sedan's body assembly alone holds 522
 * leaf parts, which is ~7,800 SVG nodes once the isometric wireframe is added —
 * enough to stall the Pi 4 target. When the cap bites the LARGEST parts are
 * kept (they carry the shape) and the count dropped is stated in the UI: a
 * silent truncation would read as "this is the whole assembly".
 */
const MAX_DRAWN = 250;

function niceScale(raw) {
  for (const s of SCALES) if (raw <= s) return s;
  return SCALES[SCALES.length - 1];
}

const fmt = (v) => (Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 10) / 10);

/** Collect the node plus every descendant that carries a bbox. */
function collectBoxes(tree, nodeId) {
  const out = [];
  if (!tree || !nodeId) return out;
  const push = (id) => {
    const n = tree.byId.get(id);
    const b = n?.bbox;
    if (b && Array.isArray(b.min) && Array.isArray(b.max)) {
      out.push({ id, name: n.name || id, min: b.min, max: b.max, leaf: !!n.leaf });
    }
  };
  const kids = tree.descendantsOf ? tree.descendantsOf(nodeId) : [];
  if (kids && kids.length) {
    for (const k of kids) {
      const n = tree.byId.get(k);
      if (n?.leaf) push(k);
    }
  }
  if (out.length === 0) push(nodeId);   // a leaf, or a node with no children
  return out;
}

/** Volume of a box, used to rank parts when the draw cap bites. */
function volume(b) {
  return Math.abs((b.max[0] - b.min[0]) * (b.max[1] - b.min[1]) * (b.max[2] - b.min[2]));
}

/** Axis pair per view: [horizontalAxis, verticalAxis, flipH, flipV]. */
const VIEWS = [
  { key: 'FRONT', h: 1, v: 2, flipH: true, flipV: true, hLabel: 'Y', vLabel: 'Z' },
  { key: 'TOP', h: 0, v: 1, flipH: false, flipV: true, hLabel: 'X', vLabel: 'Y' },
  { key: 'RIGHT', h: 0, v: 2, flipH: false, flipV: true, hLabel: 'X', vLabel: 'Z' },
];

function OrthoView({ view, boxes, bounds, scale }) {
  const { h, v, flipH, flipV } = view;
  const spanH = bounds.max[h] - bounds.min[h];
  const spanV = bounds.max[v] - bounds.min[v];

  // mm -> px, then centre the drawing inside the frame.
  const px = 1 / scale;
  const drawW = spanH * px;
  const drawH = spanV * px;
  const ox = PAD + (VIEW_W - 2 * PAD - drawW) / 2;
  const oy = PAD + (VIEW_H - 2 * PAD - drawH) / 2;

  const mapH = (val) => {
    const t = (val - bounds.min[h]) * px;
    return ox + (flipH ? drawW - t : t);
  };
  const mapV = (val) => {
    const t = (val - bounds.min[v]) * px;
    return oy + (flipV ? drawH - t : t);
  };

  return (
    <figure className="draft__view">
      <figcaption className="draft__view-title">{view.key}</figcaption>
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img"
           aria-label={`${view.key} view, ${fmt(spanH)} by ${fmt(spanV)} millimetres`}>
        {boxes.map((b) => {
          const x1 = mapH(b.min[h]);
          const x2 = mapH(b.max[h]);
          const y1 = mapV(b.min[v]);
          const y2 = mapV(b.max[v]);
          return (
            <rect
              key={b.id}
              className="draft__part"
              x={Math.min(x1, x2)}
              y={Math.min(y1, y2)}
              width={Math.max(1, Math.abs(x2 - x1))}
              height={Math.max(1, Math.abs(y2 - y1))}
            />
          );
        })}

        {/* Overall dimension, horizontal — arrows outside the drawing. */}
        <g className="draft__dim">
          <line x1={ox} y1={oy + drawH + 12} x2={ox + drawW} y2={oy + drawH + 12} />
          <line x1={ox} y1={oy + drawH + 8} x2={ox} y2={oy + drawH + 16} />
          <line x1={ox + drawW} y1={oy + drawH + 8} x2={ox + drawW} y2={oy + drawH + 16} />
          <text x={ox + drawW / 2} y={oy + drawH + 23} textAnchor="middle">
            {fmt(spanH)}
          </text>
        </g>

        {/* Overall dimension, vertical. */}
        <g className="draft__dim">
          <line x1={ox - 12} y1={oy} x2={ox - 12} y2={oy + drawH} />
          <line x1={ox - 16} y1={oy} x2={ox - 8} y2={oy} />
          <line x1={ox - 16} y1={oy + drawH} x2={ox - 8} y2={oy + drawH} />
          <text x={ox - 15} y={oy + drawH / 2}
                textAnchor="middle" transform={`rotate(-90 ${ox - 15} ${oy + drawH / 2})`}>
            {fmt(spanV)}
          </text>
        </g>
      </svg>
    </figure>
  );
}

function IsoView({ boxes, bounds, scale }) {
  const px = 1 / scale;
  // Isometric: right = (x - y)cos30, up = z + (x + y)sin30
  const project = (x, y, z) => [
    (x - y) * ISO_COS * px,
    -(z * px) + (x + y) * ISO_SIN * px * 0.5,
  ];

  const pts = [];
  for (const b of boxes) {
    for (const cx of [b.min[0], b.max[0]]) {
      for (const cy of [b.min[1], b.max[1]]) {
        for (const cz of [b.min[2], b.max[2]]) pts.push(project(cx, cy, cz));
      }
    }
  }
  if (pts.length === 0) return null;
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const minX = Math.min(...xs); const maxX = Math.max(...xs);
  const minY = Math.min(...ys); const maxY = Math.max(...ys);
  const w = maxX - minX || 1;
  const h = maxY - minY || 1;
  const k = Math.min((VIEW_W - 2 * PAD) / w, (VIEW_H - 2 * PAD) / h);
  const ox = PAD + ((VIEW_W - 2 * PAD) - w * k) / 2;
  const oy = PAD + ((VIEW_H - 2 * PAD) - h * k) / 2;
  const P = (x, y, z) => {
    const [a, b2] = project(x, y, z);
    return [ox + (a - minX) * k, oy + (b2 - minY) * k];
  };

  return (
    <figure className="draft__view">
      <figcaption className="draft__view-title">ISO</figcaption>
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} role="img" aria-label="Isometric view">
        {boxes.map((b) => {
          const c = [
            P(b.min[0], b.min[1], b.min[2]), P(b.max[0], b.min[1], b.min[2]),
            P(b.max[0], b.max[1], b.min[2]), P(b.min[0], b.max[1], b.min[2]),
            P(b.min[0], b.min[1], b.max[2]), P(b.max[0], b.min[1], b.max[2]),
            P(b.max[0], b.max[1], b.max[2]), P(b.min[0], b.max[1], b.max[2]),
          ];
          const edges = [[0, 1], [1, 2], [2, 3], [3, 0], [4, 5], [5, 6],
                         [6, 7], [7, 4], [0, 4], [1, 5], [2, 6], [3, 7]];
          return (
            <g key={b.id} className="draft__iso">
              {edges.map(([a, z]) => (
                <line key={`${a}-${z}`} x1={c[a][0]} y1={c[a][1]} x2={c[z][0]} y2={c[z][1]} />
              ))}
            </g>
          );
        })}
      </svg>
    </figure>
  );
}

export default function DraftingPanel({ nodeId, tree, manifest }) {
  const svgWrapRef = useRef(null);

  const allBoxes = useMemo(() => collectBoxes(tree, nodeId), [tree, nodeId]);
  const boxes = useMemo(() => {
    if (allBoxes.length <= MAX_DRAWN) return allBoxes;
    return [...allBoxes].sort((a, b) => volume(b) - volume(a)).slice(0, MAX_DRAWN);
  }, [allBoxes]);
  const dropped = allBoxes.length - boxes.length;

  const bounds = useMemo(() => {
    if (allBoxes.length === 0) return null;
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (const b of allBoxes) {
      for (let i = 0; i < 3; i += 1) {
        if (b.min[i] < min[i]) min[i] = b.min[i];
        if (b.max[i] > max[i]) max[i] = b.max[i];
      }
    }
    return { min, max };
  }, [allBoxes]);

  const node = nodeId && tree?.byId?.get(nodeId);

  const scale = useMemo(() => {
    if (!bounds) return 1;
    // One scale shared by all three ortho views, chosen so the largest fits.
    const need = Math.max(
      (bounds.max[0] - bounds.min[0]) / (VIEW_W - 2 * PAD),
      (bounds.max[1] - bounds.min[1]) / (VIEW_W - 2 * PAD),
      (bounds.max[2] - bounds.min[2]) / (VIEW_H - 2 * PAD),
    );
    return niceScale(need);
  }, [bounds]);

  if (!nodeId || !node) {
    return (
      <div className="draft draft--empty">
        <p>Select a part or assembly to draft it.</p>
      </div>
    );
  }
  if (!bounds) {
    return (
      <div className="draft draft--empty">
        <p>No dimensional data for <strong>{node.name || nodeId}</strong>.</p>
      </div>
    );
  }

  const dims = [
    bounds.max[0] - bounds.min[0],
    bounds.max[1] - bounds.min[1],
    bounds.max[2] - bounds.min[2],
  ];
  const dimsText = `${fmt(dims[0])} × ${fmt(dims[1])} × ${fmt(dims[2])} mm (L×W×H)`;

  const copyDims = () => {
    const lines = [
      `${node.name || nodeId}  [${nodeId}]`,
      `Overall  ${dimsText}`,
      `Extents  X ${fmt(bounds.min[0])} .. ${fmt(bounds.max[0])}`,
      `         Y ${fmt(bounds.min[1])} .. ${fmt(bounds.max[1])}`,
      `         Z ${fmt(bounds.min[2])} .. ${fmt(bounds.max[2])}`,
      `Scale    1:${scale}`,
      `Parts    ${boxes.length}${dropped > 0 ? ` of ${allBoxes.length} (${dropped} omitted)` : ''}`,
    ].join('\n');
    navigator.clipboard?.writeText(lines);
  };

  const exportSvg = () => {
    const el = svgWrapRef.current;
    if (!el) return;
    const svgs = [...el.querySelectorAll('svg')];
    if (svgs.length === 0) return;
    // Lay the views out on one sheet, two across.
    const cols = 2;
    const sheetW = cols * VIEW_W;
    const sheetH = Math.ceil(svgs.length / cols) * VIEW_H + 46;
    const body = svgs.map((s, i) => {
      const x = (i % cols) * VIEW_W;
      const y = Math.floor(i / cols) * VIEW_H;
      return `<g transform="translate(${x},${y})">${s.innerHTML}</g>`;
    }).join('');
    const title = `${node.name || nodeId}`.replace(/[<&>]/g, '');
    const sheet = `<svg xmlns="http://www.w3.org/2000/svg" width="${sheetW}" height="${sheetH}" viewBox="0 0 ${sheetW} ${sheetH}">
<style>
 rect.draft__part{fill:none;stroke:#9fc4e8;stroke-width:0.8}
 g.draft__dim line{stroke:#5d6a83;stroke-width:0.5}
 g.draft__dim text{fill:#8fa0bb;font:6px sans-serif}
 g.draft__iso line{stroke:#7fb2d8;stroke-width:0.6;fill:none}
 .tb{fill:#c8d2e4;font:9px sans-serif}
 .tb-sub{fill:#7f8da8;font:7px sans-serif}
</style>
<rect width="100%" height="100%" fill="#0B1418"/>
${body}
<g transform="translate(6,${sheetH - 30})">
 <text class="tb">${title}</text>
 <text class="tb-sub" y="12">${dimsText} · scale 1:${scale} · ${boxes.length}${dropped > 0 ? ` of ${allBoxes.length}` : ''} part(s)</text>
 <text class="tb-sub" y="22">Bounding-box extents, not true silhouettes · representative geometry, not OEM CAD</text>
</g>
</svg>`;
    const blob = new Blob([sheet], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${nodeId}.svg`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="draft">
      <header className="draft__head">
        <h3 className="draft__title">{node.name || nodeId}</h3>
        <code className="draft__id">{nodeId}</code>
      </header>

      <div className="draft__grid" ref={svgWrapRef}>
        {VIEWS.map((v) => (
          <OrthoView key={v.key} view={v} boxes={boxes} bounds={bounds} scale={scale} />
        ))}
        <IsoView boxes={boxes} bounds={bounds} scale={scale} />
      </div>

      <dl className="draft__meta">
        <div><dt>Overall</dt><dd>{dimsText}</dd></div>
        <div><dt>Scale</dt><dd>1:{scale}</dd></div>
        <div>
          <dt>Parts drawn</dt>
          <dd>
            {boxes.length}
            {dropped > 0 && (
              <span className="draft__dropped">
                {' '}of {allBoxes.length} — {dropped} smallest omitted for legibility
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt>Origin</dt>
          <dd>{manifest?.frame?.origin || 'model origin'} · +X {manifest?.frame?.x || 'forward'}</dd>
        </div>
      </dl>

      <p className="draft__caveat">
        Views are <strong>bounding-box extents</strong>, not true silhouettes —
        curved geometry is drawn as the box that contains it.
      </p>

      <div className="draft__actions">
        <button type="button" className="draft__btn" onClick={copyDims}>Copy dims</button>
        <button type="button" className="draft__btn" onClick={exportSvg}>Export SVG</button>
      </div>
    </div>
  );
}
